import React from 'react';
import {
    Box,
    Button,
    Paper,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { ConnectedDotsBackground } from '../../components/ConnectedDotsBackground';
import { LostDogIcon } from '../../components/LostDogIcon/LostDogIcon';
import { ThemeModeToggle } from '../../components/ThemeModeToggle/ThemeModeToggle';
import { CustomPaletteColor } from '../../theme';
import { setMode } from '../../state/global/globalSlice';
import { AppDispatch, RootState } from '../../state/store';
import { CLIENT_CONFIG_LOAD_ERROR_MESSAGE } from '../../constants/clientConfigFallback';

interface LoginConfigErrorProps {
    onRefresh: () => void;
    isRetrying?: boolean;
}

/** Shown inside the same login shell when `/system/client/config` cannot be loaded. */
export const LoginConfigError: React.FC<LoginConfigErrorProps> = ({ onRefresh, isRetrying = false }) => {
    const theme = useTheme();
    const isNonMobile = useMediaQuery('(min-width: 600px)');
    const dispatch = useDispatch<AppDispatch>();
    const themePreference = useSelector((state: RootState) => state.global.mode);

    return (
        <Box sx={{ display: 'block', padding: '0' }}>
            <Box sx={{
                display: 'flex',
                backgroundColor: theme.palette.background.default,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                overflowY: 'auto',
                py: 2,
            }}>
                <ConnectedDotsBackground
                    color={theme.palette.login?.dotsColor ?? theme.palette.secondary.main}
                    speed={1}
                    background={theme.palette.login?.dotsBackground
                        ?? `radial-gradient(circle at 50% 30%, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 80%)`}
                />
                <Paper elevation={1} sx={{
                    position: 'relative',
                    zIndex: 1,
                    margin: 'auto',
                    padding: isNonMobile ? '1rem' : '0.5rem',
                    width: isNonMobile ? 580 : '92%',
                    maxWidth: 580,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '20px',
                    border: `1px solid ${(theme.palette.secondary as CustomPaletteColor)[200]}`,
                }}>
                    <Toolbar sx={{ justifyContent: 'right', minHeight: '48px !important' }}>
                        <ThemeModeToggle
                            preference={themePreference}
                            onCycle={() => dispatch(setMode())}
                        />
                    </Toolbar>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            px: isNonMobile ? 3 : 2,
                            pb: 3,
                            pt: 1,
                            gap: 2,
                        }}
                    >
                        <LostDogIcon
                            sx={{
                                fontSize: 120,
                                color: theme.palette.secondary.main,
                            }}
                            aria-hidden
                        />
                        <Typography
                            variant="body1"
                            color="text.primary"
                            sx={{ maxWidth: 420, lineHeight: 1.6 }}
                        >
                            {CLIENT_CONFIG_LOAD_ERROR_MESSAGE}
                        </Typography>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<Refresh />}
                            onClick={onRefresh}
                            disabled={isRetrying}
                            sx={{ textTransform: 'none', mt: 0.5, minWidth: 140 }}
                        >
                            {isRetrying ? 'Retrying…' : 'Refresh'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};
